import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PlaceSearchInput } from '@/components/PlaceSearchInput';
import { Crosshair, LocateFixed, MapPin, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { loadGoogleMaps, geocodeAddress, reverseGeocode, extractAddressParts } from '@/lib/googleMaps';
import { toast } from 'sonner';

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

export const MapPinPicker = ({ address, city, state, pincode, lat, lng, onChange }) => {
  const mapEl = useRef(null);
  const [search, setSearch] = useState('');
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [busy, setBusy] = useState(false);
  const hasPin = Number.isFinite(lat) && Number.isFinite(lng);

  const placePin = async (pos, { reverse = true, zoom } = {}) => {
    const maps = window.google.maps;
    const map = mapRef.current;
    if (!markerRef.current) {
      markerRef.current = new maps.Marker({ map, position: pos, draggable: true, animation: maps.Animation.DROP });
      markerRef.current.addListener('dragend', (e) => placePin({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
    } else {
      markerRef.current.setPosition(pos);
    }
    map.panTo(pos);
    if (zoom) map.setZoom(zoom);
    let parts = {};
    if (reverse) {
      try { parts = extractAddressParts(await reverseGeocode(pos.lat, pos.lng)); } catch (_) { /* ignore */ }
    }
    onChangeRef.current({ lat: +pos.lat.toFixed(6), lng: +pos.lng.toFixed(6), ...parts });
  };
  const placePinRef = useRef(placePin);
  placePinRef.current = placePin;

  useEffect(() => {
    let cancelled = false;
    const onAuthFail = () => setStatus('error');
    window.addEventListener('gmaps-auth-failure', onAuthFail);
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapEl.current) return;
      const initial = hasPin ? { lat, lng } : INDIA_CENTER;
      const map = new maps.Map(mapEl.current, {
        center: initial, zoom: hasPin ? 16 : 5, mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
        gestureHandling: 'greedy', zoomControl: true, clickableIcons: false,
      });
      mapRef.current = map;
      map.addListener('click', (e) => placePinRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      if (hasPin) {
        markerRef.current = new maps.Marker({ map, position: initial, draggable: true });
        markerRef.current.addListener('dragend', (e) => placePinRef.current({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
      }
      setStatus('ready');
    }).catch(() => !cancelled && setStatus('error'));
    return () => { cancelled = true; window.removeEventListener('gmaps-auth-failure', onAuthFail); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locateAddress = async () => {
    const query = [address, city, state, pincode].filter(Boolean).join(', ');
    if (!query) return toast.error('Enter the address and city first');
    setBusy(true);
    try {
      const result = await geocodeAddress(`${query}, India`);
      if (!result) return toast.error('Address not found. Drop the pin manually on the map.');
      const pos = { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() };
      await placePin(pos, { reverse: false, zoom: 16 });
      onChangeRef.current({ lat: +pos.lat.toFixed(6), lng: +pos.lng.toFixed(6), ...extractAddressParts(result) });
      toast.success('Pin placed — drag it to the exact spot if needed');
    } catch (e) {
      toast.error('Could not locate the address. Drop the pin manually.');
    } finally { setBusy(false); }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported by this browser');
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => { await placePin({ lat: p.coords.latitude, lng: p.coords.longitude }, { zoom: 17 }); setBusy(false); },
      () => { toast.error('Location permission denied'); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="space-y-3" data-testid="map-pin-picker">
      <div className="flex flex-col sm:flex-row gap-2">
        <PlaceSearchInput value={search} onChange={setSearch} placeholder="Search a landmark, society or area to place the pin" testid="map-search-input"
          inputClassName="w-full h-10 pl-10 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          onSelect={(d) => { if (Number.isFinite(d.lat)) { placePin({ lat: d.lat, lng: d.lng }, { reverse: false, zoom: 16 }); onChangeRef.current({ lat: +d.lat.toFixed(6), lng: +d.lng.toFixed(6), city: d.city, state: d.state, pincode: d.pincode }); } }} />
        <Button type="button" variant="outline" onClick={locateAddress} disabled={busy || status !== 'ready'} data-testid="locate-address-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Crosshair className="h-4 w-4 mr-2" />} Locate address
        </Button>
        <Button type="button" variant="outline" onClick={useCurrentLocation} disabled={busy || status !== 'ready'} data-testid="use-my-location-btn">
          <LocateFixed className="h-4 w-4 mr-2" /> My location
        </Button>
      </div>

      <div className="relative rounded-md overflow-hidden border border-border/60">
        <div ref={mapEl} className="h-[340px] w-full bg-[#E5E7EB]" data-testid="pin-map" />
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading map…
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 text-sm text-red-600 p-6 text-center" data-testid="map-error">
            <AlertTriangle className="h-6 w-6 mb-2" />
            Google Maps could not be loaded. Check that the Maps JavaScript API and Places API are enabled for this key and this domain is allowed.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-sm" data-testid="pin-status">
        {hasPin ? (
          <span className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Pin set at {lat.toFixed(5)}, {lng.toFixed(5)} — drag the marker to fine-tune</span>
        ) : (
          <span className="flex items-center gap-2 text-amber-700"><AlertTriangle className="h-4 w-4" /> Pin required: click on the map, search a landmark, or use “Locate address”</span>
        )}
      </div>
    </div>
  );
};
